"use client";

import { createContext, Fragment, useCallback, useContext, useState } from "react";
import uniqueId from "lodash.uniqueid";
import { NotificationOptions, Notification } from "./types";
import Notifications from "./Notifications";
const DEFAULT_TIMEOUT = 5000;
const DEFAULT_ERROR_TIMEOUT = 10000;
interface Props {
  children: React.ReactNode;
  notificationTimeout?: number;
}
interface NotificationContextType {
  clearNotification: (id: string) => void;
  createNotification: (options: NotificationOptions) => void;
}
const defaultNotificationContext = {
  clearNotification: () => null,
  createNotification: () => null
};
const NotificationContext = createContext<NotificationContextType>(defaultNotificationContext);
export function useNotification(): NotificationContextType {
  return useContext(NotificationContext);
}
export function NotificationProvider(props: Props) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const clearNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);
  const createNotification = useCallback((options: NotificationOptions) => {
    // for error notifications, if no timeout is explicitly set, don't auto-dismiss
    const timeout = options.type === "error" && !options.timeout ? DEFAULT_ERROR_TIMEOUT : options.timeout;
    setNotifications(prev => [...prev, {
      ...options,
      timeout,
      id: uniqueId("notification-")
    }]);
  }, []);
  const notificationTimeout = props.notificationTimeout || DEFAULT_TIMEOUT;
  const value = {
    clearNotification,
    createNotification
  };
  return <Fragment>
      <Notifications notifications={notifications} onDismiss={clearNotification} timeout={notificationTimeout} data-sentry-element="Notifications" data-sentry-source-file="NotificationContext.tsx" />
      <NotificationContext.Provider value={value} data-sentry-element="unknown" data-sentry-source-file="NotificationContext.tsx">
        {props.children}
      </NotificationContext.Provider>
    </Fragment>;
}